import { GuideSection } from '@/components/guides/GuideSection';
import { GuideSteps, GuideStep } from '@/components/guides/GuideSteps';
import { GuideCallout } from '@/components/guides/GuideCallout';

export function InstallAppSection() {
  return (
    <GuideSection
      id="install"
      eyebrow="Step 1"
      title="Install the NeuraX desktop app"
      description="The app runs natively on your computer — not in a browser. You'll find the download link in the onboarding email from your recruiter."
    >
      <GuideSteps>
        <GuideStep number={1} title="Find the download link in your email">
          <p>
            Your recruiter sent you an onboarding email when they added you as a candidate. Open it;
            near the top you'll see a button like <em>"Download NeuraX"</em> with a link. Click it.
          </p>
        </GuideStep>

        <GuideStep number={2} title="Pick your platform">
          <p>The download page offers two installers:</p>
          <ul className="ml-4 list-disc space-y-1">
            <li>
              <strong>macOS</strong> — a <code>.dmg</code> file. Works on Apple Silicon
              (M1/M2/M3/M4) and Intel Macs running macOS 13 Ventura or newer.
            </li>
            <li>
              <strong>Windows</strong> — a <code>.exe</code> installer. Works on Windows 10 and 11,
              64-bit.
            </li>
          </ul>
        </GuideStep>

        <GuideStep number={3} title="Run the installer">
          <p>
            <strong>On macOS:</strong> double-click the <code>.dmg</code> to mount it, then drag the
            NeuraX icon into your Applications folder. Eject the installer.
          </p>
          <p>
            <strong>On Windows:</strong> double-click the <code>.exe</code> and follow the installer
            prompts. Accept when Windows asks for permission.
          </p>
        </GuideStep>

        <GuideStep number={4} title="Launch NeuraX">
          <p>
            Open the app from Applications (macOS) or the Start menu (Windows). The first launch
            will take a few seconds while it initializes.
          </p>
        </GuideStep>
      </GuideSteps>

      <GuideCallout variant="warning" title="If you can't find the email">
        <p>
          Check your spam folder. If it's still missing, reply to your recruiter — they can resend
          it with one click. Don't try to download NeuraX from a random search result; the official
          installer is only linked from the onboarding email or a page your recruiter shares with
          you.
        </p>
      </GuideCallout>

      <GuideCallout variant="info" title="About updates">
        <p>
          NeuraX updates itself in the background when a new version ships. You'll see a small
          notice the next time you launch it; just click <em>Restart to update</em> when it's
          convenient. If you're a week away from a critical interview, let it update now — don't
          defer.
        </p>
      </GuideCallout>
    </GuideSection>
  );
}
